import { Paypal } from '@df/module-paypal/paypal.namespace';

declare global {
    interface Window {
        google?: {
            payments?: {
                api?: GooglePay.IApi;
            };
        };
    }
}

export namespace GooglePay {
    export enum Scripts {
        pay = 'https://pay.google.com/gp/p/js/pay.js'
    }

    export enum Environments {
        production = 'PRODUCTION',
        test = 'TEST' // default
    }

    export interface IApi {
        PaymentsClient: IApiPaymentsClientConstructable;
    }

    export type IApiPaymentsClientConstructable = new (options: IPaymentOptions) => IPaymentsClientInstance;

    export interface IPaymentsClientInstance {
        // The convenience method is used to generate a Google Pay payment button styled
        // with the latest Google Pay branding for insertion into a webpage.
        createButton(options: IButtonOptions): HTMLButtonElement;
        // Use the isReadyToPay(isReadyToPayRequest) method
        // to determine a user's ability to return a form of payment from the Google Pay API.
        isReadyToPay(isReadyToPayRequest: IIsReadyToPayRequest): Promise<IIsReadyToPayResponse>;
        // This method presents a Google Pay payment sheet that allows selection of
        // a payment method and optionally configured parameters.
        loadPaymentData(paymentDataRequest: IPaymentDataRequest): Promise<IPaymentData>;
        // Use this method to prefetch a PaymentDataRequest configuration
        // to improve loadPaymentData execution time on later user interaction. No value is returned.
        // Prefetch optimizations are currently available on Chrome for Android. Other browsers and platforms to follow.
        prefetchPaymentData(paymentDataRequest: IPaymentDataRequest): void;
        // This method is called when a payment is authorized in the payment sheet.
        onPaymentAuthorized(paymentData: IPaymentData): Promise<IPaymentAuthorizationResult>;
        // This method handles payment data changes in the payment sheet such as shipping address and shipping options.
        onPaymentDataChanged(intermediatePaymentData: IIntermediatePaymentData): Promise<IPaymentDataRequestUpdate>;
    }

    export interface IPaymentOptions {
        environment?: Environments;
        // This object provides information about the merchant that requests payment data.
        MerchantInfo?: IMerchantInfo;
        // This object declares the callbacks used for Dynamic Price Updates.
        paymentDataCallbacks?;
    }

    export interface IMerchantInfo {
        merchantId: string;
        merchantName?: string;
    }

    export interface IPaymentDataCallbacks {
        // Invoked when the following callbackIntents values are set in PaymentDataRequest:
        // SHIPPING_ADDRESS
        // SHIPPING_OPTION
        onPaymentDataChanged?: onPaymentDataChangedCallback;
        // Invoked when the following callbackIntents values are set in PaymentDataRequest:
        // PAYMENT_AUTHORIZATION
        onPaymentAuthorized: onPaymentAuthorizedCallback;
    }

    export type onPaymentDataChangedCallback = (intermediatePaymentData: IIntermediatePaymentData) => Promise<IPaymentDataRequestUpdate>;

    export type onPaymentAuthorizedCallback = (paymentData: IPaymentData) => Promise<IPaymentAuthorizationResult>;

    export interface IIntermediatePaymentData {
        // Describes the reason for which payment data callback was invoked.
        callbackTrigger?: IntermediatePaymentDataCallbackTrigger;
        // The selected address in the payment sheet.
        shippingAddress?: IIntermediateAddress;
        // The selected shipping option in the payment sheet.
        shippingOptionData?: ISelectionOptionData;
    }

    export enum IntermediatePaymentDataCallbackTrigger {
        INITIALIZE = 'INITIALIZE',
        SHIPPING_ADDRESS = 'SHIPPING_ADDRESS',
        SHIPPING_OPTION = 'SHIPPING_OPTION'
    }

    export interface IIntermediateAddress {
        // A country subdivision, such as a state or province.
        administrativeArea: string;
        // ISO 3166-1 alpha-2 country code.
        countryCode: string;
        // City, town, neighborhood, or suburb.
        locality: string;
        // The redacted postal code based on the country. For Canada and the UK,
        // this contains only the first three characters. For US, this contains the first five digits.
        postalCode: string;
    }

    export interface ISelectionOptionData {
        id: string;
    }

    export interface IPaymentDataRequestUpdate {
        // Updates the transaction info in the payment sheet.
        newTransactionInfo?: ITransactionInfo;
        // Updates the shipping options in the payment sheet.
        newShippingOptionParameters?: IShippingOptionParameters;
        // Adds an error message to the payment sheet.
        error?: IPaymentDataError;
    }

    export interface ITransactionInfo {
        // ISO 4217 alphabetic currency code.
        currencyCode: string;
        // ISO 3166-1 alpha-2 country code where the transaction is processed.
        // This is required for merchants based in European Economic Area (EEA) countries.
        countryCode?: string;
        // A unique ID that identifies a transaction attempt.
        // Merchants may use an existing ID or generate a specific one for Google Pay transaction attempts.
        // This field is required when you send callbacks to the Google Transaction Events API.
        transactionId?: string;
        // The status of the total price used
        totalPriceStatus: TotalPriceStatusType;
        // Total monetary value of the transaction with an optional decimal precision of two decimal places.
        // This field is required unless totalPriceStatus is set to NOT_CURRENTLY_KNOWN.
        // The format of the string should follow the regex format: ^[0-9]+(\.[0-9][0-9])?$
        totalPrice: string;
        // All of the available charges for the current payment request.
        // This is only populated in the payment sheet if you use Authorize Payments or Dynamic Price Updates.
        // This field is required if you implement support for Authorize Payments or Dynamic Price Updates.
        displayItems?: IDisplayItem[];
        // Custom label for the total price within the display items.
        totalPriceLabel?: string;
        // Affects the submit button text displayed in the Google Pay payment sheet.
        checkoutOption?: CheckoutOptionType;
    }

    export enum TotalPriceStatusType {
        // Used for a capability check. Do not use this property if the transaction is processed in an EEA country.
        NOT_CURRENTLY_KNOWN = 'NOT_CURRENTLY_KNOWN',
        // Total price may adjust based on the details of the response, such as sales tax collected based on a billing address.
        ESTIMATED = 'ESTIMATED',
        // Total price doesn't change from the amount presented to the shopper.
        FINAL = 'FINAL'
    }

    export enum CheckoutOptionType {
        // Standard text applies for the given totalPriceStatus (default).
        DEFAULT = 'DEFAULT',
        // The selected payment method is charged immediately after the payer confirms their selections.
        // This option is only available when totalPriceStatus is set to FINAL.
        COMPLETE_IMMEDIATE_PURCHASE = 'COMPLETE_IMMEDIATE_PURCHASE'
    }
    export interface IDisplayItem {
        // The label to be displayed for the given option.
        label: string;
        type: DisplayItemType;
        price: string;
        status?: DisplayItemStatus;
    }

    export enum DisplayItemType {
        LINE_ITEM = 'LINE_ITEM',
        SUBTOTAL = 'SUBTOTAL'
    }

    export enum DisplayItemStatus {
        // default
        FINAL = 'FINAL',
        PENDING = 'PENDING'
    }

    export interface IShippingOptionParameters {
        // An identifier to the default selected shipping option.
        // If this field isn't provided, the first option is the default option.
        defaultSelectedOptionId?: string;
        // All of the shipping options available for the current request.
        shippingOptions: ISelectionOption | ISelectionOption[];
    }

    export interface ISelectionOption {
        // The developer can put any value that needs to be returned in PaymentData.
        id: string;
        // The label to be displayed as the option.
        label: string;
        // A descriptive text that is displayed below the option label.
        description: string;
    }

    export interface IPaymentDataError {
        reason: PaymentDataErrorReason | string;
        // Error message to the user that's displayed in a dialog.
        message: string;
        // The intent of the error.
        intent: PaymentDataRequestIntent;
    }

    export enum PaymentDataErrorReason {
        PAYMENT_DATA_INVALID = 'PAYMENT_DATA_INVALID',
        SHIPPING_ADDRESS_INVALID = 'SHIPPING_ADDRESS_INVALID',
        SHIPPING_ADDRESS_UNSERVICEABLE = 'SHIPPING_ADDRESS_UNSERVICEABLE',
        SHIPPING_OPTION_INVALID = 'SHIPPING_OPTION_INVALID',
        OTHER_ERROR = 'OTHER_ERROR'
    }

    export interface IPaymentData {
        // Major API version. The value in the response matches the value provided in PaymentDataRequest.
        apiVersion: number;
        // Minor API version. The value in the response matches the value provided in PaymentDataRequest.
        apiVersionMinor: number;
        // Data about the selected payment method.
        paymentMethodData: IPaymentMethodData;
        // Email address, if emailRequired is set to true in the PaymentDataRequest.
        // If another request has the property set to true there's no effect.
        email?: string;
        // Shipping address, if shippingAddressRequired is set to true in the PaymentDataRequest.
        shippingAddress?: IAddress;
    }

    // This object provides information about a requested postal address. All the properties are strings.
    // A MIN address format may be returned if billingAddressFormat is set to MIN.
    // A shipping address is returned in the FULL address format.
    // All properties in a MIN formatted response exist in a FULL formatted response.
    export interface IAddress {
        // MIN:
        // The full name of the addressee.
        name: string;
        // The postal or ZIP code.
        postalCode: string;
        // ISO 3166-1 alpha-2 country code.
        countryCode: string;
        // A telephone number, if phoneNumberRequired is set to true in the PaymentDataRequest.
        phoneNumber: string;
        // FULL:
        // The first line of the address.
        address1?: string;
        // The second line of the address.
        address2?: string;
        // The third line of the address.
        address3?: string;
        // City, town, neighborhood, or suburb.
        locality?: string;
        // A country subdivision, such as a state or province.
        administrativeArea?: string;
        // The sorting code.
        sortingCode?: string;
    }

    export interface IPaymentMethodData {
        // PaymentMethod type selected in the Google Pay payment sheet.
        type: string;
        // User-facing message to describe the payment method that funds this transaction.
        description: string;
        // The value of this property depends on the payment method type returned.
        info: ICardInfo;
        // Payment tokenization data for the selected payment method.
        tokenizationData?: IPaymentMethodTokenizationData;
    }

    export interface ICardInfo {
        // The details about the card. This value is commonly the last four digits of the selected payment account number.
        cardDetails: string;
        // This object provides information about the validation performed on the returned payment data
        // if assuranceDetailsRequired is set to true in the CardParameters.
        assuranceDetails: IAssuranceDetailsSpecifications;
        // The payment card network of the selected payment. Returned values match the format of allowedCardNetworks in CardParameters.
        // This card network value should not be displayed to the buyer. It's used when the details of a buyer's card are needed.
        // For example, if customer support needs this value to identify the card a buyer used for their transaction.
        // For a user-visible description, use the description property of PaymentMethodData instead.
        cardNetwork: string;
        // The billing address associated with the provided payment method, if billingAddressRequired is set to true in CardParameters.
        billingAddress?: IAddress;
    }

    export interface IAssuranceDetailsSpecifications {
        // If true, indicates that Cardholder possession validation has been performed on returned payment credential.
        accountVerified: boolean;
        // If true, indicates that identification and verifications (ID&V) was performed on the returned payment credential.
        // If false, the same risk-based authentication can be performed as you would for card transactions.
        // This risk-based authentication can include, but not limited to, step-up with 3D Secure protocol if applicable.
        cardHolderAuthenticated: boolean;
    }

    export interface IPaymentMethodTokenizationData {
        // The type of tokenization to be applied to the selected payment method.
        // This value matches the type set in PaymentMethodTokenizationSpecification.
        type: string;
        // The generated payment method token.
        token?: string;
    }

    export interface IPaymentAuthorizationResult {
        transactionState: PaymentAuthorizationResultStatus;
        error?: IPaymentDataError;
    }

    export enum PaymentAuthorizationResultStatus {
        SUCCESS = 'SUCCESS',
        ERROR = 'ERROR'
    }

    export interface IIsReadyToPayRequest {
        // Major API version. The value is 2 for this specification.
        apiVersion: number;
        // Minor API version. The value is 0 for this specification.
        apiVersionMinor: number;
        // Specifies support for one or more payment methods supported by the Google Pay API.
        // A tokenizationSpecification isn't required to determine a viewer's readiness to pay.
        // Provide all required parameters properties for each supported PaymentMethod.
        allowedPaymentMethods: IPaymentMethod[];
        // If set to true then the IsReadyToPayResponse object includes an additional property
        // that describes the visitor's readiness to pay with one or more payment methods specified in allowedPaymentMethods.
        existingPaymentMethodRequired?: boolean;
    }

    export interface IPaymentMethod {
        // A short identifier for the supported payment method. Only CARD and PAYPAL currently are supported entries.
        type: PaymentMethodType;
        // Parameters required to configure the provided payment method type
        parameters: ICardParameters | IPayPalParameters;
        tokenizationSpecification?: ITokenizationSpecification;
    }

    export enum PaymentMethodType {
        CARD = 'CARD',
        PAYPAL = 'PAYPAL'
    }

    export interface ICardParameters {
        // Fields supported to authenticate a card transaction.
        allowedAuthMethods: CardParametersAllowedAuthMethods[];
        // One or more card networks that you support, also supported by the Google Pay API.
        allowedCardNetworks: string[];
        // Set to false if you don't support prepaid cards.
        // Default: The prepaid card class is supported for the card networks specified.
        allowPrepaidCards?: boolean;
        // Set to false if you don't support credit cards.
        // Default: The credit card class is supported for the card networks specified.
        allowCreditCards?: boolean;
        // Set to true to request assuranceDetails.
        // This object provides information about the validation performed on the returned payment data.
        assuranceDetailsRequired?: boolean;
        // Set to true if you require a billing address.
        // A billing address should only be requested if it's required to process the transaction.
        // Additional data requests can increase friction in the checkout process and lead to a lower conversion rate.
        billingAddressRequired?: boolean;
        billingAddressParameters?: IBillingAddressParameters;
    }

    export enum CardParametersAllowedAuthMethods {
        // This authentication method is associated with payment cards stored on file with the user's Google Account.
        // Returned payment data includes personal account number (PAN) with the expiration month and the expiration year.
        PAN_ONLY = 'PAN_ONLY',
        // This authentication method is associated with cards stored as Android device tokens.
        // Returned payment data includes a 3-D Secure (3DS) cryptogram generated on the device.
        CRYPTOGRAM_3DS = 'CRYPTOGRAM_3DS'
    }

    export interface IBillingAddressParameters {
        // Billing address format required to complete the transaction.
        format?: AddressType;
        // Set to true if a phone number is required to process the transaction.
        phoneNumberRequired?: boolean;
    }

    export enum AddressType {
        // Name, country code, and postal code (default).
        MIN = 'MIN',
        // Name, street address, locality, region, country code, and postal code.
        FULL = 'FULL'
    }

    export interface IPayPalParameters {
        // Use information about the order for the description.
        purchase_context: IPurchaseContext;
    }

    export interface IPurchaseContext {
        // Describes the contract between the customer and the merchant.
        purchase_units: IPurchaseUnit[];
    }

    export interface IPurchaseUnit {
        // The receipt of funds for this transaction.
        payee: Paypal.IPayee;
    }

    export interface ITokenizationSpecification {
        type: PaymentMethodTokenizationType;
        // Parameters specific to the selected payment method tokenization type.
        parameters: {};
    }

    export enum PaymentMethodTokenizationType {
        PAYMENT_GATEWAY = 'PAYMENT_GATEWAY',
        DIRECT = 'DIRECT'
    }

    export interface IPaymentDataRequest {
        apiVersion: number;
        apiVersionMinor: number;
        // Information about the merchant that requests payment data.
        merchantInfo: IMerchantInfo;
        // Specifies support for one or more payment methods supported by the Google Pay API.
        allowedPaymentMethods: IPaymentMethod[];
        // Details about the authorization of the transaction based upon whether the user agrees to the transaction or not.
        // Includes total price and price status.
        transactionInfo: ITransactionInfo;
        // Specifies the following callback intents for PaymentDataCallbacks:
        callbackIntents?: PaymentDataRequestIntent[];
        // Set to true to request an email address.
        emailRequired?: boolean;
        // Set to true to request a full shipping address.
        shippingAddressRequired?: boolean;
        shippingAddressParameters?: IShippingAddressParameters;
        // Set to true when the SHIPPING_OPTION callback intent is used.
        // This field is required if you implement support for Authorize Payments or Dynamic Price Updates.
        // For details, see ShippingOptionParameters.
        shippingOptionRequired?: boolean;
        shippingOptionParameters?: IShippingOptionParameters[];
    }

    export enum PaymentDataRequestIntent {
        PAYMENT_AUTHORIZATION = 'PAYMENT_AUTHORIZATION',
        SHIPPING_ADDRESS = 'SHIPPING_ADDRESS',
        SHIPPING_OPTION = 'SHIPPING_OPTION'
    }

    export interface IShippingAddressParameters {
        // ISO 3166-1 alpha-2 country code values of the countries where shipping is allowed.
        // If this object isn't specified, all shipping address countries are allowed.
        allowedCountryCodes?: string[];
        // Set to true if a phone number is required for the provided shipping address.
        phoneNumberRequired?: boolean;
    }

    export interface IButtonOptions {
        onClick: (event) => void;
        buttonColor?: ButtonOptionsColor;
        buttonType?: ButtonOptionsType;
        buttonSizeMode?: ButtonOptionsSizeMode;
        buttonRootNode?: HTMLDocument;
    }

    export enum ButtonOptionsColor {
        default = 'default',
        black = 'black',
        white = 'white'
    }

    export enum ButtonOptionsType {
        buy = 'buy',
        donate = 'donate',
        plain = 'planin'
    }

    export enum ButtonOptionsSizeMode {
        static = 'static',
        fill = 'fill'
    }

    export interface IPaymentsError {
        // Short code that describes the type of error.
        statusCode: string;
        // Developer-facing message that describes the error encountered and possible steps to correct it.
        statusMessage: string;
    }

    export enum PaymentsErrorExamples {
        // The current Google user is unable to provide payment information.
        BUYER_ACCOUNT_ERROR = 'BUYER_ACCOUNT_ERROR',
        // A passed parameter is improperly formatted.
        // An error message may appear in the browser console for all configured environments.
        DEVELOPER_ERROR = 'DEVELOPER_ERROR',
        // The site accessing the Google Pay API does not have the right permission.
        // This could be due to either an incorrect configuration or an incorrect merchant identifier set in the request.
        // Check the statusMessage field for more details. If you continue to have trouble, please contact support.
        MERCHANT_ACCOUNT_ERROR = 'MERCHANT_ACCOUNT_ERROR',
        // General server error.
        INTERNAL_ERROR = 'INTERNAL_ERROR'
    }

    export interface IIsReadyToPayResponse {
        result: boolean;
        // If true, the visitor has one or more payment methods as specified in the allowedPaymentMethods property
        // of the provided IsReadyToPayRequest.
        // Exists only when existingPaymentMethodRequired was set to true in IsReadyToPayRequest.
        // If PaymentsClient is initialized with an environment property of TEST a payment method is always considered present.
        paymentMethodPresent?: boolean;
    }

    export type TCardType = 'AMEX' | 'DISCOVER' | 'JCB' | 'MASTERCARD' | 'VISA';
}
